import React from "react";
import { motion } from "framer-motion";
import {
  Check,
  Crown,
  Briefcase,
  Heart,
  GraduationCap,
  Flame,
  Medal,
  IdCard,
  Plane,
  Users,
} from "lucide-react";
import HeaderV2 from "./HeaderV2";
import FooterV2 from "./FooterV2";
import V2PageHero from "./sections/V2PageHero";
import FAQV2 from "./sections/FAQV2";
import CTAV2 from "./sections/CTAV2";
import InlineCTA from "./sections/InlineCTA";
import useSmoothScroll from "../../hooks/useSmoothScroll";

const VISA_TYPES = [
  {
    icon: Crown,
    title: "Golden Visa (10 years)",
    who: "Investors · Specialised talent · Entrepreneurs",
    copy: "Long-term self-sponsored residency with multiple-entry and family coverage.",
  },
  {
    icon: Briefcase,
    title: "Investor / Partner Visa (2 years)",
    who: "Shareholders in UAE entities",
    copy: "Renewable residency for founders and shareholders of UAE companies.",
  },
  {
    icon: Flame,
    title: "Green Visa (5 years)",
    who: "Freelancers · Self-employed · Skilled employees",
    copy: "Self-sponsored 5-year residency with expanded family sponsorship rights.",
  },
  {
    icon: Heart,
    title: "Family Residence Visa",
    who: "Spouse · Children · Parents",
    copy: "Bring your loved ones to the UAE — attestation, medical and Emirates ID included.",
  },
  {
    icon: GraduationCap,
    title: "Student & Virtual Work",
    who: "Students & remote professionals",
    copy: "Virtual working program and student residence for the next generation of talent.",
  },
  {
    icon: Medal,
    title: "Talent & Specialised Visa",
    who: "Scientists · Doctors · Athletes · Creators",
    copy: "Federal and emirate-level talent pathways for outstanding professionals.",
  },
];

const GOLDEN = [
  "10-year UAE Golden Visa for eligible investors & entrepreneurs",
  "Self-sponsored residency — no employer dependency",
  "Sponsor immediate family, parents and household staff",
  "Priority airport, banking and government services",
  "Fast-track processing via senior StartINC liaisons",
  "Renewal, status-change and compliance handled end-to-end",
];

const PROCESS = [
  { icon: IdCard, t: "Eligibility Mapping", d: "We identify your fastest visa pathway in 24 hours." },
  { icon: Plane, t: "Entry Permit & Status", d: "Entry permit issued, in-country status change handled." },
  { icon: Users, t: "Medical & Emirates ID", d: "Medical fitness, biometrics, Emirates ID — escorted end-to-end." },
  { icon: Crown, t: "Visa Stamping", d: "Residency stamped on passport. Family sponsorships unlocked." },
];

export default function VisaV2() {
  useSmoothScroll(true);
  return (
    <div data-testid="v2-visa-page" className="bg-white">
      <HeaderV2 />
      <main>
        <V2PageHero
          eyebrow="Visa & Residency"
          title="Your gateway to"
          highlight="UAE residency."
          copy="From investor and Golden Visas to employee work permits and family sponsorship — we handle the full immigration lifecycle so you can focus on building."
          image="https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&w=2400&q=80"
        />

        {/* Visa types */}
        <section className="bg-white py-10 md:py-14">
          <div className="container-x">
            <div className="max-w-2xl mb-7">
              <p className="overline text-[#3366FF]">Residency pathways</p>
              <div className="gold-rule mt-5 mb-6" />
              <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
                Six UAE residency pathways —{" "}
                <span className="font-black text-[#002366]">one of them is yours</span>.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {VISA_TYPES.map(({ icon: Icon, title, who, copy }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
                  className="rounded-sm border border-slate-200 bg-white p-7 hover:shadow-xl hover:border-[#3366FF]/40 transition-all group"
                >
                  <div className="w-11 h-11 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-4 font-heading font-semibold text-base md:text-lg tracking-tight text-[#0F172A]">
                    {title}
                  </h3>
                  <p className="mt-1 text-xs font-heading font-bold tracking-widest uppercase text-[#3366FF]">
                    {who}
                  </p>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{copy}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <InlineCTA
          eyebrow="Not sure which visa fits?"
          title="A 20-minute call clarifies your fastest residency pathway."
          buttonLabel="Talk to an advisor"
          service="visa"
        />

        {/* Golden Visa */}
        <section className="bg-[#F8FAFC] py-10 md:py-14">
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <p className="overline text-[#3366FF]">The Golden Visa</p>
              <div className="gold-rule mt-5 mb-6" />
              <h2 className="font-heading font-light tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] text-[#0F172A] leading-tight">
                10 years of residency.{" "}
                <span className="font-black text-[#002366]">Unconditional</span> mobility.
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                The UAE Golden Visa is a long-term residence permit for investors,
                entrepreneurs, specialised talent and outstanding students. StartINC
                identifies your most efficient pathway and prepares every document
                to a government-grade standard.
              </p>
            </div>
            <div className="lg:col-span-6">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {GOLDEN.map((t) => (
                  <li key={t} className="flex gap-3 text-slate-700 bg-white border border-slate-200 rounded-sm p-4">
                    <Check className="w-5 h-5 shrink-0 text-[#3366FF] mt-0.5" />
                    <span className="text-sm md:text-base">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="bg-white py-10 md:py-14">
          <div className="container-x">
            <div className="max-w-2xl mb-7">
              <p className="overline text-[#3366FF]">How we handle your visa</p>
              <div className="gold-rule mt-5 mb-6" />
              <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-2xl sm:text-3xl lg:text-[2.5rem] leading-tight">
                A 4-step <span className="font-black text-[#002366]">white-glove</span> immigration process.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {PROCESS.map(({ icon: Icon, t, d }, i) => (
                <motion.div
                  key={t}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="p-6 rounded-sm border border-slate-200 bg-white hover:shadow-md hover:border-[#3366FF]/40 transition-all relative"
                >
                  <div className="w-10 h-10 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="absolute top-5 right-5 font-heading font-black text-3xl text-slate-200">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-heading font-semibold text-[#0F172A]">{t}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <FAQV2 />
        <CTAV2 />
      </main>
      <FooterV2 />
    </div>
  );
}
